/**
 * §P.4 · Facturación: la remesa, los recibos y los cobros.
 *
 * ## El alcance, y por qué es más pequeño de lo que parece
 *
 * H-14 y H-17 dejaron la domiciliación fuera: **no hay pasarela y no se guarda dato
 * bancario**. El cobro se hace por otro lado —transferencia, Bizum, efectivo— y lo
 * anota a mano el dueño del club. Así que aquí no hay fichero SEPA, ni mandatos, ni
 * devoluciones; eso es fase 2, con la domiciliación.
 *
 * Lo que sí hay es la cadena entera de «quién me debe qué»: emitir los recibos del
 * mes, verlos, anotar lo que entra y saber a quién llamar.
 *
 * ## Lo que no se calcula aquí
 *
 * **Nada de dinero.** `paid_cents` viene calculado del servidor —es la suma de los
 * cobros confirmados, no una columna acumulada—, así que lo pendiente es una resta
 * de dos números que ya vienen dados. Y el plan de la remesa lo recalcula el
 * servidor al lanzar: si la pantalla mandara el suyo se cobraría lo que el navegador
 * tenía hace diez minutos, con un descuento que caducó ayer.
 */

export type EstadoDeRecibo =
  'draft' | 'issued' | 'paid' | 'failed' | 'refunded' | 'void';

/**
 * Cómo se pagó.
 *
 * `sepa` y `card` existen en el contrato y **no se ofrecen**: no hay domiciliación
 * ni pasarela. Un club que marca «tarjeta» a mano está anotando algo que nadie puede
 * conciliar con un extracto.
 */
export type MetodoDeCobro = 'sepa' | 'card' | 'cash' | 'transfer' | 'bizum';

export const METODOS_A_MANO: readonly {
  valor: MetodoDeCobro;
  etiqueta: string;
  pista: string;
}[] = [
  {
    valor: 'transfer',
    etiqueta: 'Transferencia',
    pista: 'El concepto que aparece en el extracto.',
  },
  {
    valor: 'bizum',
    etiqueta: 'Bizum',
    pista: 'Los últimos dígitos del teléfono, o el concepto.',
  },
  {
    valor: 'cash',
    etiqueta: 'Efectivo',
    pista: '«En mano el martes», por ejemplo.',
  },
];

export const etiquetaDeMetodo = (metodo: string): string =>
  ({
    transfer: 'Transferencia',
    bizum: 'Bizum',
    cash: 'Efectivo',
    sepa: 'Domiciliación',
    card: 'Tarjeta',
  })[metodo] ?? metodo;

export const etiquetaDeEstado = (estado: string): string =>
  ({
    draft: 'Borrador',
    issued: 'Emitido',
    paid: 'Pagado',
    failed: 'Devuelto',
    refunded: 'Devuelto al pagador',
    void: 'Anulado',
  })[estado] ?? estado;

/** Por qué una cuota se queda fuera de la remesa. */
export const etiquetaDeSalto = (motivo: string): string =>
  ({
    already_invoiced: 'Ya tiene recibo de este mes',
    period_not_supported: 'Tarifa trimestral o anual',
  })[motivo] ?? motivo;

export const explicacionDeSalto = (motivo: string): string =>
  ({
    already_invoiced:
      'No hay nada que hacer: ya se le emitió. Lanzar otra vez no lo duplica.',
    period_not_supported:
      'Hoy la remesa solo sabe de cuotas mensuales. Esta hay que emitirla a mano como recibo suelto.',
  })[motivo] ?? '';

export type TipoDeLinea =
  | 'subscription'
  | 'exam'
  | 'competition'
  | 'camp'
  | 'material'
  | 'license'
  | 'manual'
  | 'discount';

export const TIPOS_DE_LINEA: readonly {
  valor: TipoDeLinea;
  etiqueta: string;
}[] = [
  { valor: 'manual', etiqueta: 'Otro concepto' },
  { valor: 'camp', etiqueta: 'Campamento' },
  { valor: 'exam', etiqueta: 'Examen' },
  { valor: 'competition', etiqueta: 'Competición' },
  { valor: 'material', etiqueta: 'Material' },
  { valor: 'license', etiqueta: 'Licencia' },
  { valor: 'discount', etiqueta: 'Descuento (resta)' },
];

export interface Recibo {
  readonly id: string;
  readonly payer_id: string;
  readonly payer_name: string;
  readonly billing_run_id: string | null;
  readonly period: string | null;
  readonly number: string | null;
  readonly issue_date: Date | string;
  readonly due_date: Date | string | null;
  readonly subtotal_cents: number;
  readonly discount_cents: number;
  readonly tax_cents: number;
  readonly total_cents: number;
  /** Suma de los cobros confirmados. **La calcula el servidor.** */
  readonly paid_cents: number;
  readonly currency: string;
  readonly status: EstadoDeRecibo;
}

export interface LineaDeRecibo {
  readonly id: string;
  readonly kind: string;
  readonly subscription_id: string | null;
  readonly student_id: string | null;
  readonly description: string;
  readonly quantity: number;
  readonly unit_amount_cents: number;
  /** Negativo en las líneas de descuento. */
  readonly amount_cents: number;
}

export interface Cobro {
  readonly id: string;
  readonly invoice_id: string;
  readonly provider: string;
  readonly method: MetodoDeCobro;
  readonly reference: string | null;
  readonly recorded_by: string | null;
  readonly amount_cents: number;
  readonly status: string;
  readonly charge_date: Date | string | null;
  readonly settled_at: Date | string | null;
  readonly created_at: Date | string;
}

export interface DetalleDeRecibo {
  readonly invoice: Recibo;
  readonly lines: readonly LineaDeRecibo[];
  readonly payments: readonly Cobro[];
}

export interface Moroso {
  readonly payer_id: string;
  readonly payer_name: string;
  readonly payer_email: string | null;
  readonly invoice_count: number;
  readonly pending_cents: number;
  /** `null` si nada ha vencido todavía. **Es lo que convierte la lista en una decisión.** */
  readonly oldest_due_days: number | null;
  readonly students: readonly string[];
}

export interface Remesa {
  readonly id: string;
  readonly period: string;
  readonly status: 'pending' | 'running' | 'completed' | 'failed';
  readonly invoices_created: number;
  readonly invoices_skipped: number;
  readonly total_cents: number;
  readonly started_at: Date | string | null;
  readonly finished_at: Date | string | null;
  readonly error: string | null;
  readonly created_at: Date | string;
}

export interface LineaPlanificada {
  readonly subscription_id: string;
  readonly student_id: string;
  readonly student_name: string;
  readonly fee_plan: string;
  readonly base_cents: number;
  readonly amount_cents: number;
  readonly discount_codes: readonly string[];
}

export interface ReciboPlanificado {
  readonly payer_id: string;
  readonly payer_name: string;
  readonly lines: readonly LineaPlanificada[];
  readonly subtotal_cents: number;
  readonly discount_cents: number;
  readonly total_cents: number;
}

export interface CuotaSaltada {
  readonly subscription_id: string;
  readonly payer_id: string;
  readonly payer_name: string;
  readonly student_name: string;
  readonly reason: string;
}

export interface PlanDeRemesa {
  readonly period: string;
  readonly invoices: readonly ReciboPlanificado[];
  readonly skipped: readonly CuotaSaltada[];
  readonly total_cents: number;
}

export interface PaginaDeRecibos {
  readonly data: readonly Recibo[];
  readonly next_cursor: string | null;
  readonly has_more: boolean;
}

/**
 * Lo que queda por cobrar de un recibo. **Dos números que ya vienen dados.**
 *
 * Y **cero si está anulado**, aunque la resta diga otra cosa. Un recibo anulado
 * conserva su `total_cents` —no se borra nunca, y el importe es lo que explica el
 * documento— pero nadie lo debe. Sin esta línea la ficha de un recibo anulado de
 * 120 € decía «PENDIENTE 120,00 €» y la familia salía en morosos por algo que el
 * club ya había cancelado. Se vio en el navegador, justo después de anular uno.
 */
export const pendienteDe = (r: Recibo): number =>
  r.status === 'void' ? 0 : r.total_cents - r.paid_cents;

const MESES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

/**
 * `2026-09` → «septiembre de 2026».
 *
 * A mano y no con `Intl`: un `new Date('2026-09')` es medianoche **UTC** del día 1, y
 * al oeste de Greenwich eso es agosto. Un mes de facturación equivocado no es un
 * detalle de formato — es un recibo del mes que no toca.
 */
export function etiquetaDePeriodo(period: string | null): string {
  if (!period) return '—';
  const [año, mes] = period.split('-');
  const i = Number.parseInt(mes ?? '', 10) - 1;
  if (!año || Number.isNaN(i) || i < 0 || i > 11) return period;
  return `${MESES[i]} de ${año}`;
}

/** El mes en curso, `YYYY-MM`, en local. Es el que se propone. */
export function periodoActual(hoy = new Date()): string {
  return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`;
}

/** El mes anterior a uno dado. Para el desplegable del selector. */
export function periodoAnterior(period: string): string {
  const [año, mes] = period.split('-').map((n) => Number.parseInt(n, 10));
  if (!año || !mes) return period;
  if (mes === 1) return `${año - 1}-12`;
  return `${año}-${String(mes - 1).padStart(2, '0')}`;
}

/**
 * Los periodos que se ofrecen: el mes en curso y los cinco anteriores.
 *
 * No se ofrece el futuro. Emitir el recibo de noviembre en septiembre es un error de
 * tecleo con consecuencias —una familia recibe un cargo que no esperaba— y el
 * desplegable es el sitio más barato para impedirlo.
 */
export function periodosOfrecidos(hoy = new Date(), cuantos = 6): string[] {
  const lista: string[] = [];
  let p = periodoActual(hoy);
  for (let i = 0; i < cuantos; i += 1) {
    lista.push(p);
    p = periodoAnterior(p);
  }
  return lista;
}

/**
 * Cuánto lleva debiéndose, en palabras.
 *
 * Los días sueltos por encima de un mes no se leen: «74 días» hay que dividirlo
 * mentalmente. Y por debajo sí, porque la diferencia entre 3 y 20 días es la
 * diferencia entre esperar y llamar.
 *
 * `null` es «todavía no ha vencido», que **no** es cero días: uno es un recibo que
 * aún no toca y el otro es uno que vence hoy.
 */
export function antiguedad(dias: number | null): string {
  if (dias === null) return 'Todavía no ha vencido';
  if (dias <= 0) return 'Vence hoy';
  if (dias === 1) return 'Desde ayer';
  if (dias < 31) return `Desde hace ${dias} días`;

  const meses = Math.floor(dias / 30);
  return meses === 1 ? 'Desde hace un mes' : `Desde hace ${meses} meses`;
}

/** A partir de dos meses la fila se marca: es una conversación distinta. */
export const esDeudaVieja = (dias: number | null): boolean =>
  dias !== null && dias >= 60;

/* ── §P.4 fase 2 · Devoluciones ──────────────────────────────────────────── */

/**
 * Una devolución.
 *
 * ## Cuelga del **cobro**, no del recibo
 *
 * Y esa es la decisión que evita mandar dinero a la cuenta equivocada: «se devuelve
 * el Bizum de marzo, no *marzo*». Un recibo puede tener dos cobros —medio en efectivo
 * y medio por transferencia— y devolver el que no era es un error que se descubre en
 * el extracto del banco, no en el panel.
 *
 * ## Se puede hacer sin pasarela, y por eso está
 *
 * `POST /refunds` no habla con ningún proveedor: registra que el club devolvió el
 * dinero, igual que `POST /payments` registra que entró. Con H-14 —sin pasarela— el
 * dinero sale por Bizum o transferencia y **esto es el apunte**. No hace falta ni IBAN
 * ni identificador de acreedor, que es lo que H-17 dejó fuera.
 */
export interface Devolucion {
  readonly id: string;
  /** El cobro devuelto. Ver la cabecera. */
  readonly payment_id: string;
  readonly invoice_id: string;
  readonly invoice_number: string | null;
  readonly payer_name: string;
  readonly amount_cents: number;
  readonly reason: string | null;
  readonly status: string;
  readonly created_by: string | null;
  readonly created_at: Date | string;
}

/**
 * Cuánto queda por devolver de un cobro.
 *
 * `null` cuando no se sabe: esta cuenta necesita las devoluciones de **ese** cobro, y
 * si no se han cargado no se inventa un tope. Devolver «todo lo que queda» es el caso
 * normal y lo resuelve el servidor sin `amount_cents`; esto es solo para no ofrecer un
 * importe que se sabe imposible.
 */
export function quedaPorDevolver(
  cobro: { id: string; amount_cents: number },
  devoluciones: readonly Devolucion[] | undefined
): number | null {
  if (!devoluciones) return null;

  const devuelto = devoluciones
    .filter((d) => d.payment_id === cobro.id && d.status !== 'failed')
    .reduce((suma, d) => suma + d.amount_cents, 0);

  return Math.max(cobro.amount_cents - devuelto, 0);
}

/** Un cobro **confirmado** es lo único que se puede devolver. */
export const sePuedeDevolver = (cobro: { status: string }): boolean =>
  cobro.status === 'confirmed';

/**
 * Los tres estados de una devolución, **comprobados contra el `CHECK` de la tabla**:
 * `pending`, `confirmed` y `failed`.
 *
 * La primera versión de este mapa decía `processing` y `completed`, que no existen, y
 * el panel pintaba «confirmed» en crudo. Es la segunda vez en este proyecto que
 * escribo una tabla de estados de memoria —la otra fueron las acciones del registro
 * de accesos—, así que aquí queda dicho: **estas salen del `CHECK`.**
 *
 * Hoy solo se ve `confirmed`, porque sin pasarela (H-14) el club anota una devolución
 * que **ya ha hecho** por Bizum o transferencia: el `INSERT` la escribe confirmada.
 * Los otros dos están en el esquema para cuando haya domiciliación, y se traducen
 * porque el día que aparezcan no queremos enterarnos por una pastilla en inglés.
 */
export const etiquetaDeEstadoDeDevolucion = (estado: string): string =>
  ({
    pending: 'Pendiente de salir',
    confirmed: 'Devuelto',
    failed: 'Falló',
  })[estado] ?? estado;

export const pastillaDeDevolucion = (estado: string): string =>
  ({
    pending: 'pastilla-paused',
    confirmed: 'pastilla-bien',
    failed: 'pastilla-mal',
  })[estado] ?? 'pastilla';
